/**
 * Image Upload Validation Utility
 * Provides comprehensive validation for profile image uploads
 * Supports file type, size, and format validation with user-friendly error messages
 */

export interface ImageValidationResult {
  isValid: boolean;
  error?: string;
  errorType?: 'FILE_TYPE' | 'FILE_SIZE' | 'FILE_FORMAT' | 'FILE_MISSING';
}

export interface ImageValidationOptions {
  maxSizeBytes?: number;
  allowedTypes?: string[];
  allowedExtensions?: string[];
}

/**
 * Default validation options for profile images
 */
export const DEFAULT_IMAGE_VALIDATION_OPTIONS: ImageValidationOptions = {
  maxSizeBytes: 5 * 1024 * 1024, // 5MB
  allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
};

const EXTENSION_TO_MIME: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp'
};

/**
 * Builds allowedExtensions / allowedTypes from a server CSV (e.g. "jpg,png,webp").
 */
export function profileFormatsCsvToImageOptions(csv: string | undefined | null): Pick<ImageValidationOptions, 'allowedExtensions' | 'allowedTypes'> {
  const raw = (csv && csv.trim()) || 'jpg,jpeg,png,gif,webp';
  const parts = raw
    .split(',')
    .map((s) => s.trim().toLowerCase().replace(/^\./, ''))
    .filter(Boolean);
  const allowedExtensions = [...new Set(parts.map((p) => `.${p}`))];
  const allowedTypes = [...new Set(allowedExtensions.map((e) => EXTENSION_TO_MIME[e]).filter(Boolean))];
  return {
    allowedExtensions: allowedExtensions.length > 0 ? allowedExtensions : DEFAULT_IMAGE_VALIDATION_OPTIONS.allowedExtensions!,
    allowedTypes: allowedTypes.length > 0 ? allowedTypes : DEFAULT_IMAGE_VALIDATION_OPTIONS.allowedTypes!
  };
}

/**
 * Validates an image file for upload
 * @param file - The file to validate
 * @param options - Validation options (optional)
 * @returns ImageValidationResult with validation status and error details
 */
export const validateImageFile = (
  file: File | null | undefined,
  options: ImageValidationOptions = DEFAULT_IMAGE_VALIDATION_OPTIONS
): ImageValidationResult => {
  // Check if file exists
  if (!file) {
    return {
      isValid: false,
      error: 'Please select an image file to upload.',
      errorType: 'FILE_MISSING'
    };
  }

  const {
    maxSizeBytes = DEFAULT_IMAGE_VALIDATION_OPTIONS.maxSizeBytes!,
    allowedTypes = DEFAULT_IMAGE_VALIDATION_OPTIONS.allowedTypes!,
    allowedExtensions = DEFAULT_IMAGE_VALIDATION_OPTIONS.allowedExtensions!
  } = options;

  // Validate file type
  const isValidType = allowedTypes.includes(file.type.toLowerCase());
  
  // Validate file extension as backup
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

  if (!isValidType && !hasValidExtension) {
    const allowedFormats = allowedExtensions.map(ext => ext.replace('.', '').toUpperCase()).join(', ');
    return {
      isValid: false,
      error: `Invalid file type. Please select a valid image file (${allowedFormats}).`,
      errorType: 'FILE_TYPE'
    };
  }

  // Validate file size
  if (file.size > maxSizeBytes) {
    const maxSizeMB = (maxSizeBytes / (1024 * 1024)).toFixed(1);
    const currentSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      error: `Image size must be less than ${maxSizeMB}MB. Current size: ${currentSizeMB}MB.`,
      errorType: 'FILE_SIZE'
    };
  }

  // Additional format validation for common issues
  if (file.type === '' && !hasValidExtension) {
    return {
      isValid: false,
      error: 'Unable to determine file type. Please ensure you are uploading a valid image file.',
      errorType: 'FILE_FORMAT'
    };
  }

  return {
    isValid: true
  };
};

/**
 * Validates multiple image files
 * @param files - Array of files to validate
 * @param options - Validation options (optional)
 * @returns Array of validation results
 */
export const validateImageFiles = (
  files: File[],
  options: ImageValidationOptions = DEFAULT_IMAGE_VALIDATION_OPTIONS
): ImageValidationResult[] => {
  return files.map(file => validateImageFile(file, options));
};

/**
 * Gets user-friendly error message for validation result
 * @param result - Validation result
 * @returns User-friendly error message
 */
export const getValidationErrorMessage = (result: ImageValidationResult): string => {
  if (result.isValid) return '';
  
  return result.error || 'Invalid image file. Please try again.';
};

/**
 * Checks if file is a valid image by attempting to load it
 * @param file - File to check
 * @returns Promise that resolves to true if image is valid
 */
export const isValidImageFile = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    
    img.src = url;
  });
};

/**
 * Gets image dimensions from file
 * @param file - Image file
 * @returns Promise with image dimensions
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
};

/**
 * Formats file size for display
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Creates a preview URL for an image file
 * @param file - Image file
 * @returns Object URL for preview (remember to revoke when done)
 */
export const createImagePreview = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Revokes an image preview URL
 * @param url - Object URL to revoke
 */
export const revokeImagePreview = (url: string): void => {
  URL.revokeObjectURL(url);
};